import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Table,
  DatePicker,
  Button,
  Space,
  Typography,
  Statistic,
  Row,
  Col,
  AutoComplete,
  Input,
  Select,
  Tag,
  Modal,
  List,
  Divider,
  Spin,
  message,
} from 'antd';
import {
  UserOutlined,
  ReloadOutlined,
  SearchOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  CameraOutlined,
  DownloadOutlined,
  EyeOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import { formatDateTime } from '../../utils/helpers';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const StyledCard = styled(Card)`
  border-radius: 16px !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  margin-bottom: 12px;

  .ant-card-head {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const StatCard = styled(motion.div)`
  background: ${props => props.$gradient};
  border-radius: 16px;
  padding: 24px;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  height: 100%;

  .stat-value {
    font-size: 36px;
    font-weight: 700;
    margin: 8px 0;
  }

  .stat-label {
    font-size: 14px;
    opacity: 0.9;
  }
`;

const TableWrapper = styled.div`
  .ant-table-body {
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 4px;
    }

    &:hover::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

const SearchInputWrapper = styled.div`
  .ant-select-auto-complete {
    .ant-select-selector {
      border-radius: 12px !important;
      border: 2px solid #e8e8e8 !important;
      padding: 8px 16px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
      height: 44px !important;
      background: #ffffff;

      &:hover {
        border-color: #B4A0F7 !important;
        box-shadow: 0 4px 12px rgba(180, 160, 247, 0.15) !important;
      }

      .ant-select-selection-search-input {
        font-size: 15px;
        height: 100% !important;

        &::placeholder {
          color: #a0a0a0;
          font-weight: 400;
        }
      }

      .ant-select-selection-placeholder {
        color: #a0a0a0;
        font-size: 15px;
      }
    }

    &.ant-select-focused .ant-select-selector {
      border-color: #B4A0F7 !important;
      box-shadow: 0 4px 16px rgba(180, 160, 247, 0.25) !important;
    }
  }
`;

const CrossingsReport = () => {
  const [loading, setLoading] = useState(false);
  const [crossingsData, setCrossingsData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [empIdFilter, setEmpIdFilter] = useState('');
  const [cameraFilter, setCameraFilter] = useState(null);
  const thresholdSeconds = 30; // Fixed threshold, not editable
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [crossingDetails, setCrossingDetails] = useState([]);
  const [topCrossers, setTopCrossers] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Autocomplete state
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [searchingEmployees, setSearchingEmployees] = useState(false);

  // Debounce timer refs
  const suggestionDebounceTimer = useRef(null);
  const isSearchingRef = useRef(false);
  const lastSearchValue = useRef('');

  const fetchCameras = useCallback(async () => {
    try {
      const data = await api.getCameras();
      setCameras(data.cameras || []);
    } catch (error) {
      console.error('Error fetching cameras:', error);
    }
  }, []);

  const fetchCrossingsData = useCallback(async (empId = empIdFilter) => {
    try {
      setLoading(true);
      const dateStr = selectedDate?.format('YYYY-MM-DD');

      const data = await api.getCrossingsReport(
        empId || null,
        dateStr || null,
        dateStr || null,
        cameraFilter,
        thresholdSeconds,
        1000  // Maximum allowed limit
      );

      setCrossingsData(data.crossings || []);
      setTotalRecords(data.total_records || 0);
      setPage(1);  // Reset to first page on new search
    } catch (error) {
      console.error('Error fetching crossings data:', error);
      setCrossingsData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, cameraFilter, thresholdSeconds, empIdFilter]);

  const handleSearchEmployees = useCallback((searchText) => {
    // Clear previous timer
    if (suggestionDebounceTimer.current) {
      clearTimeout(suggestionDebounceTimer.current);
    }

    if (!searchText || searchText.length < 2) {
      setEmployeeSuggestions([]);
      lastSearchValue.current = '';
      return;
    }

    // Skip if already searching or same value
    if (isSearchingRef.current || lastSearchValue.current === searchText) {
      return;
    }

    // Debounce the search to reduce API calls
    suggestionDebounceTimer.current = setTimeout(async () => {
      // Double check before making API call
      if (isSearchingRef.current || lastSearchValue.current === searchText) {
        return;
      }

      try {
        isSearchingRef.current = true;
        lastSearchValue.current = searchText;
        setSearchingEmployees(true);

        const result = await api.getEmployeeList({
          search: searchText,
          limit: 10,
          offset: 0,
        });

        // Filter results to only include employees that actually match the search term
        const searchLower = searchText.toLowerCase();
        const filteredData = result.data.filter((emp) => {
          const empIdMatch = emp.emp_id?.toLowerCase().includes(searchLower);
          const nameMatch = emp.full_name?.toLowerCase().includes(searchLower);
          const emailMatch = emp.email?.toLowerCase().includes(searchLower);
          return empIdMatch || nameMatch || emailMatch;
        });

        // Limit to top 5 matches
        const topMatches = filteredData.slice(0, 5);

        const suggestions = topMatches.map((emp) => ({
          value: emp.emp_id,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span><strong>{emp.emp_id}</strong> - {emp.full_name}</span>
              {emp.department && <Tag color="blue" style={{ marginLeft: 8 }}>{emp.department}</Tag>}
            </div>
          ),
        }));

        setEmployeeSuggestions(suggestions);
      } catch (error) {
        console.error('Error searching employees:', error);
        setEmployeeSuggestions([]);
      } finally {
        setSearchingEmployees(false);
        isSearchingRef.current = false;
      }
    }, 400); // 400ms delay to reduce API calls while typing
  }, []);

  const fetchTopCrossers = useCallback(async () => {
    try {
      const dateStr = selectedDate?.format('YYYY-MM-DD') || null;
      const data = await api.getTopCrossers(10, dateStr, dateStr);
      setTopCrossers(data.top_crossers || []);
    } catch (error) {
      console.error('Error fetching top crossers:', error);
      setTopCrossers([]);
    }
  }, [selectedDate]);

  // Initial load only
  useEffect(() => {
    fetchCameras();
    fetchCrossingsData();
    fetchTopCrossers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPDFReport = async () => {
    try {
      const dateStr = selectedDate?.format('YYYY-MM-DD') || null;

      // Build query parameters
      const params = new URLSearchParams({
        threshold_seconds: thresholdSeconds.toString()
      });

      if (empIdFilter) params.append('emp_id', empIdFilter);
      if (dateStr) {
        params.append('start_date', dateStr);
        params.append('end_date', dateStr);
      }
      if (cameraFilter) params.append('camera_id', cameraFilter);

      // Show loading message
      const hideLoading = message.loading('Generating PDF...', 0);

      try {
        // Start async generation
        const response = await fetch(
          `http://localhost:8000/crossings/generate-report-async?${params.toString()}`,
          { method: 'POST' }
        );

        const result = await response.json();

        if (result.cached) {
          // PDF was cached, download immediately
          hideLoading();
          message.success('PDF ready (from cache)!');
          window.open(`http://localhost:8000/crossings/download-async/${result.task_id}`, '_blank');
          return;
        }

        // Poll for progress
        const task_id = result.task_id;
        let progress = 0;

        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`http://localhost:8000/crossings/task-status/${task_id}`);
            const status = await statusRes.json();

            progress = status.progress;

            if (status.status === 'completed') {
              clearInterval(pollInterval);
              hideLoading();
              message.success('PDF generated successfully!');
              window.open(`http://localhost:8000/crossings/download-async/${task_id}`, '_blank');
            } else if (status.status === 'failed') {
              clearInterval(pollInterval);
              hideLoading();
              message.error(`PDF generation failed: ${status.error}`);
            } else {
              // Update loading message with progress
              message.loading(`Generating PDF... ${progress}%`, 0);
            }
          } catch (err) {
            console.error('Error polling status:', err);
          }
        }, 1000); // Poll every second

        // Timeout after 2 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          hideLoading();
          message.warning('PDF generation is taking longer than expected. Check back later.');
        }, 120000);

      } catch (err) {
        hideLoading();
        console.error('Error generating PDF:', err);
        message.error('Failed to start PDF generation');
      }

    } catch (error) {
      console.error('Error downloading PDF report:', error);
      message.error('Failed to download PDF report');
    }
  };

  const showEmployeeDetails = async (emp_id) => {
    try {
      setLoading(true);
      const dateStr = selectedDate?.format('YYYY-MM-DD') || null;

      const data = await api.getEmployeeCrossings(emp_id, dateStr, dateStr);
      setSelectedEmployee(data.summary);
      setCrossingDetails(data.crossing_details || []);
      setDetailsModalVisible(true);
    } catch (error) {
      console.error('Error fetching crossing details:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Employee ID',
      dataIndex: 'emp_id',
      key: 'emp_id',
      fixed: 'left',
      width: 120,
      render: (text) => <span style={{ fontWeight: 500, color: '#6366F1' }}>{text}</span>,
    },
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 180,
      sorter: (a, b) => (a.full_name || '').localeCompare(b.full_name || ''),
    },
    {
      title: 'Zone (Last Seen)',
      dataIndex: 'last_seen_zone',
      key: 'last_seen_zone',
      width: 150,
      render: (last_seen_zone) => {
        // Show the actual last seen zone from the most recent crossing
        const lastZone = last_seen_zone || 'N/A';
        return (
          <Tag
            icon={<CameraOutlined />}
            bordered
            color="blue"
            style={{
              borderRadius: '4px',
              fontSize: '12px',
              padding: '2px 8px',
              fontWeight: 500
            }}
          >
            {lastZone}
          </Tag>
        );
      },
      sorter: (a, b) => {
        const zoneA = a.last_seen_zone || '';
        const zoneB = b.last_seen_zone || '';
        return zoneA.localeCompare(zoneB);
      },
    },
    {
      title: 'Crossings',
      dataIndex: 'crossing_count',
      key: 'crossing_count',
      width: 120,
      sorter: (a, b) => a.crossing_count - b.crossing_count,
      defaultSortOrder: 'descend',
      render: (count) => (
        <Tag
          bordered
          color="green"
          style={{
            borderRadius: '4px',
            fontSize: '12px',
            padding: '2px 8px',
            fontWeight: 500
          }}
        >
          {count} times
        </Tag>
      ),
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 150,
      sorter: (a, b) => (a.department || '').localeCompare(b.department || ''),
    },
    {
      title: 'Unique Tracks',
      dataIndex: 'unique_tracks',
      key: 'unique_tracks',
      width: 120,
      sorter: (a, b) => a.unique_tracks - b.unique_tracks,
    },
    {
      title: 'Total Frames',
      dataIndex: 'total_frames',
      key: 'total_frames',
      width: 120,
      sorter: (a, b) => a.total_frames - b.total_frames,
    },
    {
      title: 'First Crossing',
      dataIndex: 'first_crossing',
      key: 'first_crossing',
      width: 180,
      render: (time) => (
        <span style={{ color: '#6B7280', fontSize: '13px' }}>
          {formatDateTime(time)}
        </span>
      ),
    },
    {
      title: 'Last Crossing',
      dataIndex: 'last_crossing',
      key: 'last_crossing',
      width: 180,
      render: (time) => (
        <span style={{ color: '#6B7280', fontSize: '13px' }}>
          {formatDateTime(time)}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => showEmployeeDetails(record.emp_id)}
          style={{ color: '#6366F1' }}
        />
      ),
    },
  ];

  const totalCrossings = crossingsData.reduce((sum, item) => sum + item.crossing_count, 0);
  const avgCrossingsPerEmployee = crossingsData.length > 0 ? (totalCrossings / crossingsData.length).toFixed(1) : 0;

  return (
    <div>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginLeft: 0, marginRight: 0, marginBottom: 12 }}>
        <Col span={8}>
          <StatCard
            $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            whileHover={{ scale: 1.02 }}
          >
            <div className="stat-label">Total Employees</div>
            <div className="stat-value">{crossingsData.length}</div>
          </StatCard>
        </Col>
        <Col span={8}>
          <StatCard
            $gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
            whileHover={{ scale: 1.02 }}
          >
            <div className="stat-label">Total Crossings</div>
            <div className="stat-value">{totalCrossings}</div>
          </StatCard>
        </Col>
        <Col span={8}>
          <StatCard
            $gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
            whileHover={{ scale: 1.02 }}
          >
            <div className="stat-label">Avg per Employee</div>
            <div className="stat-value">{avgCrossingsPerEmployee}</div>
          </StatCard>
        </Col>
      </Row>

      {/* Filters Toggle Button */}
      <div style={{ marginBottom: 12 }}>
        <Button
          icon={<FilterOutlined />}
          onClick={() => setShowFilters(!showFilters)}
          size="large"
          style={{
            borderRadius: '8px',
            height: '48px',
            borderColor: showFilters ? '#B4A0F7' : '#e8e8e8',
            color: showFilters ? '#B4A0F7' : undefined
          }}
        >
          Filters
        </Button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <StyledCard>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Text strong>Date</Text>
                <DatePicker
                  value={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                />
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Text strong style={{ color: '#262626', fontSize: '14px' }}>Employee Search</Text>
                <SearchInputWrapper>
                  <AutoComplete
                    value={empIdFilter}
                    options={employeeSuggestions}
                    onSearch={handleSearchEmployees}
                    onSelect={(value) => setEmpIdFilter(value)}
                    onChange={(value) => setEmpIdFilter(value)}
                    placeholder="Search by ID or name..."
                    style={{ width: '100%' }}
                    notFoundContent={searchingEmployees ? <Spin size="small" /> : null}
                    popupMatchSelectWidth={400}
                    allowClear
                  />
                </SearchInputWrapper>
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Text strong>Camera</Text>
                <Select
                  placeholder="All Cameras"
                  value={cameraFilter}
                  onChange={setCameraFilter}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {cameras.map((cam) => (
                    <Option key={cam.camera_id} value={cam.camera_id}>
                      Camera {cam.camera_id} {cam.zone ? `- ${cam.zone}` : ''}
                    </Option>
                  ))}
                </Select>
              </Space>
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => {
                    fetchCrossingsData();
                    fetchTopCrossers();
                  }}
                >
                  Apply Filters
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setEmpIdFilter('');
                    setCameraFilter(null);
                    setSelectedDate(dayjs());
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={downloadPDFReport}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none'
                  }}
                >
                  Download PDF Report
                </Button>
              </Space>
            </Col>
          </Row>
        </StyledCard>
      )}

      {/* Top Crossers */}
      <Row gutter={[16, 16]} style={{ display: 'flex' }}>
        <Col span={8} style={{ display: 'flex' }}>
          <StyledCard
            title={
              <Space>
                <TrophyOutlined style={{ color: '#FFD700' }} />
                <span>Top 10 Crossers</span>
              </Space>
            }
            style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            bodyStyle={{ flex: 1, overflow: 'auto' }}
          >
            <List
              dataSource={topCrossers}
              renderItem={(item, index) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background:
                            index === 0
                              ? '#FFD700'
                              : index === 1
                              ? '#C0C0C0'
                              : index === 2
                              ? '#CD7F32'
                              : '#E0E0E0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          color: '#fff',
                        }}
                      >
                        {index + 1}
                      </div>
                    }
                    title={<Text strong>{item.full_name || item.emp_id}</Text>}
                    description={`${item.emp_id} • ${item.crossing_count} crossings`}
                  />
                </List.Item>
              )}
            />
          </StyledCard>
        </Col>

        <Col span={16} style={{ display: 'flex' }}>
          {/* Main Table */}
          <StyledCard
            title={`Crossings Report (${totalRecords} employees)`}
            extra={
              <Text type="secondary">
                Date: {selectedDate?.format('MMM DD, YYYY')}
              </Text>
            }
            style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            bodyStyle={{ flex: 1, overflow: 'auto' }}
          >
            <TableWrapper>
              <Table
                size="small"
                columns={columns}
                dataSource={crossingsData}
                rowKey="emp_id"
                loading={loading}
                scroll={{ x: 1500, y: 'calc(100vh - 380px)' }}
                pagination={{
                current: page,
                pageSize: pageSize,
                total: crossingsData.length,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} employees`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (newPage, newPageSize) => {
                  setPage(newPage);
                  if (newPageSize !== pageSize) {
                    setPageSize(newPageSize);
                    setPage(1);
                  }
                },
                responsive: true,
              }}
              />
            </TableWrapper>
          </StyledCard>
        </Col>
      </Row>

      {/* Details Modal */}
      <Modal
        title={
          <Space>
            <UserOutlined />
            <span>
              Crossing Details: {selectedEmployee?.full_name || selectedEmployee?.emp_id}
            </span>
          </Space>
        }
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedEmployee && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Statistic title="Total Crossings" value={selectedEmployee.crossing_count} />
              </Col>
              <Col span={6}>
                <Statistic title="Unique Tracks" value={selectedEmployee.unique_tracks} />
              </Col>
              <Col span={6}>
                <Statistic title="Total Frames" value={selectedEmployee.total_frames} />
              </Col>
              <Col span={6}>
                <Text type="secondary">Employee ID:</Text>
                <br />
                <Text strong>{selectedEmployee.emp_id}</Text>
              </Col>
            </Row>

            <Divider>Individual Crossings</Divider>

            <List
              dataSource={crossingDetails}
              renderItem={(crossing, index) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          background: '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                        }}
                      >
                        #{index + 1}
                      </div>
                    }
                    title={
                      <Space>
                        <ClockCircleOutlined />
                        <Text strong>{formatDateTime(crossing.entry_time)}</Text>
                        <Tag color="blue">Track ID: {crossing.track_id}</Tag>
                        <Tag color="green">
                          <CameraOutlined /> {crossing.zone || crossing.camera_id}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <Text type="secondary">
                          Duration: {crossing.duration_seconds.toFixed(1)}s • Frames: {crossing.frame_count}
                        </Text>
                        <Space>
                          <Text type="secondary">Emotions:</Text>
                          {crossing.emotions?.map((emotion, idx) => (
                            <Tag key={idx}>{emotion}</Tag>
                          ))}
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default CrossingsReport;
